import type {
  Conversation,
  ConversationMessage,
} from '@/features/conversations/api';
import type { CallTranscriptLine } from '@/features/voice-receptionist/api';

/** The message that records a lead-form submission: the card replaces it. */
export function isLeadFormSubmission(message: ConversationMessage): boolean {
  const metadata = message.metadata as Record<string, unknown> | null;
  return Boolean(metadata && 'leadFormSubmission' in metadata);
}

/** The lead a conversation belongs to: the FK, else the legacy metadata pointer. */
export function conversationLeadId(conversation: Conversation): string | null {
  return (
    conversation.leadId ??
    (conversation.metadata as { leadId?: string } | null)?.leadId ??
    null
  );
}

export interface HandoffInfo {
  reason: string | null;
  detail: string | null;
  at: string | null;
}

/** The conversation's current hand-off, or null when nobody has been asked. */
export function conversationHandoff(
  conversation: Conversation
): HandoffInfo | null {
  const m = conversation.metadata as {
    escalationReason?: string;
    escalationDetail?: string;
    escalatedAt?: string;
  } | null;
  if (!m?.escalationReason) return null;
  return {
    reason: m.escalationReason,
    detail: m.escalationDetail ?? null,
    at: m.escalatedAt ?? null,
  };
}

/** Calls stay anchored to their start time, independent of transcript loading. */
export function anchorCalls<
  C extends { id: string; startedAt?: string | null },
>(
  messages: ConversationMessage[],
  calls: C[]
): { byMessageId: Map<string, C[]>; trailing: C[] } {
  const byMessageId = new Map<string, C[]>();
  const trailing: C[] = [];
  const callIds = new Set(calls.map((call) => call.id));
  const visible = messages.filter(
    (message) =>
      !callIds.has(callSidOf(message) ?? '') && !isLeadFormSubmission(message)
  );
  // A call keeps its place when transcript batches or recording data arrive.
  const orderedCalls = [...calls].sort(
    (a, b) =>
      Date.parse(a.startedAt ?? '') - Date.parse(b.startedAt ?? '') ||
      a.id.localeCompare(b.id)
  );
  for (const call of orderedCalls) {
    const at = Date.parse(call.startedAt ?? '');
    const next = Number.isFinite(at)
      ? visible.find(
          (message) => Date.parse(message.sentAt ?? message.createdAt) >= at
        )
      : undefined;
    if (next) {
      const group = byMessageId.get(next.id) ?? [];
      group.push(call);
      byMessageId.set(next.id, group);
    } else {
      trailing.push(call);
    }
  }
  return { byMessageId, trailing };
}

/** Transcript messages already shown inside a call card. */
export function callSidOf(message: ConversationMessage): string | null {
  return (
    (message.metadata as { voiceCallSid?: string } | null)?.voiceCallSid ?? null
  );
}

export function callTranscriptPreviews(messages: ConversationMessage[]) {
  const previews = new Map<string, CallTranscriptLine[]>();
  for (const message of messages) {
    const sid = callSidOf(message);
    if (!sid || (message.role !== 'user' && message.role !== 'bot')) continue;
    const lines = previews.get(sid) ?? [];
    lines.push({
      id: message.id,
      speaker: message.role === 'user' ? 'caller' : 'agent',
      text: message.content,
      at: message.sentAt ?? message.createdAt,
    });
    previews.set(sid, lines);
  }
  return previews;
}

export interface TeamFollowUp {
  need: 'price' | 'details';
  serviceName: string;
  askedAt: string;
}

/**
 * What Claire told the customer our team will send (a price or what a
 * treatment involves) that nobody on the team has replied to since. Claire
 * keeps the conversation, so this is the only sign in the inbox that someone
 * owes the customer an answer. `lastAgentReplyAt` is when a team member last
 * wrote in the thread.
 */
export function openTeamFollowUps(
  conversation: Pick<Conversation, 'metadata'>,
  lastAgentReplyAt: string | null | undefined
): TeamFollowUp[] {
  const asked = (conversation.metadata as { teamFollowUps?: unknown } | null)
    ?.teamFollowUps;
  if (!Array.isArray(asked)) return [];
  const repliedAt = lastAgentReplyAt
    ? Date.parse(lastAgentReplyAt)
    : Number.NaN;
  return asked.filter(
    (r): r is TeamFollowUp =>
      typeof r === 'object' &&
      r !== null &&
      (r.need === 'price' || r.need === 'details') &&
      typeof r.serviceName === 'string' &&
      typeof r.askedAt === 'string' &&
      !(repliedAt > Date.parse(r.askedAt))
  );
}

/** When a team member last wrote in the thread, or null. */
export function lastAgentReplyAt(
  messages: readonly ConversationMessage[]
): string | null {
  let latest: string | null = null;
  for (const m of messages) {
    if (m.role !== 'agent') continue;
    const at = m.sentAt ?? m.createdAt;
    if (!latest || Date.parse(at) > Date.parse(latest)) latest = at;
  }
  return latest;
}

/** "Needs a price: Lip Filler" — the row badge and the card's lines. */
export function teamFollowUpLabel(request: TeamFollowUp): string {
  return request.need === 'price'
    ? `Needs a price: ${request.serviceName}`
    : `Needs details: ${request.serviceName}`;
}
